import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Radio, RefreshCw, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SIGNAL_PROVIDERS, PROVIDER_COLORS, UNSET_LABEL } from '@/components/operation/signalProviders';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORDER = [...SIGNAL_PROVIDERS, UNSET_LABEL];
const fmt = (v, suffix = '') => (v == null ? '—' : `${v}${suffix}`);
const signed = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v}`);

const Toggle = ({ value, onChange, options, testid }) => (
  <div className="inline-flex rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] p-0.5" data-testid={testid}>
    {options.map(([v, label]) => (
      <button key={v} onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
        className={`px-3 min-h-9 text-xs font-semibold rounded-md transition-colors ${value === v ? 'bg-[#E63946] text-white' : 'text-[#9CA3AF] hover:text-white'}`}>
        {label}
      </button>
    ))}
  </div>
);

const ProviderCard = ({ name, r }) => {
  const color = PROVIDER_COLORS[name];
  const empty = !r;
  if (empty) {
    return (
      <div className="rounded-xl border p-4 bg-[hsl(var(--surface-elevated))] opacity-60 flex items-center justify-between" style={{ borderColor: `${color}55` }}
        data-testid={`provider-card-${name || 'unset'}`}>
        <span className="flex items-center gap-2 text-sm font-bold text-white"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />{name}</span>
        <span className="text-xs text-[#9CA3AF]">Tiada signal dalam tempoh ini</span>
      </div>
    );
  }
  return (
    <div className="rounded-xl border p-4 bg-[hsl(var(--surface-elevated))]" style={{ borderColor: `${color}55` }}
      data-testid={`provider-card-${name || 'unset'}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="flex items-center gap-2 text-sm font-bold text-white"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />{name || UNSET_LABEL}</span>
        <span className="text-xs text-[#9CA3AF]">{r ? `${r.signals} signal` : 'Tiada signal'}</span>
      </div>
      <p className="text-xs uppercase tracking-wide text-[#9CA3AF]">Win rate</p>
      <p className={`text-3xl font-black ${r?.win_rate >= 70 ? 'text-emerald-400' : r?.win_rate >= 50 ? 'text-amber-400' : r ? 'text-red-400' : 'text-[#6B7280]'}`}
        data-testid={`provider-winrate-${name || 'unset'}`}>{fmt(r?.win_rate, '%')}</p>
      <div className="grid grid-cols-3 gap-1 text-xs mt-2 text-[#9CA3AF]">
        <span>Win <b className="text-emerald-400">{r?.win ?? 0}</b></span>
        <span>Loss <b className="text-red-400">{r?.loss ?? 0}</b></span>
        <span>BE <b className="text-amber-400">{r?.breakeven ?? 0}</b></span>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3 text-xs text-[#9CA3AF]">
        <div><p>Net pips</p><p className={`text-base font-bold ${(r?.net_pips || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid={`provider-pips-${name || 'unset'}`}>{signed(r?.net_pips)}</p></div>
        <div><p>Purata / signal</p><p className="text-base font-bold text-white">{signed(r?.avg_pips)}</p></div>
      </div>
      <div className="mt-3">
        <div className="flex justify-between text-xs text-[#9CA3AF]"><span>Sumbangan pips</span><span className="text-white font-semibold" data-testid={`provider-share-${name || 'unset'}`}>{fmt(r?.pips_share_pct, '%')}</span></div>
        <div className="h-1.5 mt-1 rounded-full bg-white/5 overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, r?.pips_share_pct || 0))}%`, backgroundColor: color }} />
        </div>
      </div>
    </div>
  );
};

const TrendChart = ({ trend, metric, names }) => {
  const data = useMemo(() => trend.map((t) => ({
    period: t.period, ...Object.fromEntries(names.map((n) => [n || UNSET_LABEL, t.values[n]?.[metric] ?? null])),
  })), [trend, metric, names]);
  if (!data.length) return <p className="text-sm text-[#6B7280] text-center py-10" data-testid="provider-trend-empty">Tiada signal dalam tempoh ini.</p>;
  return (
    <div className="h-[280px]" data-testid="provider-trend-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ left: -10, right: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis dataKey="period" stroke="#9CA3AF" tick={{ fontSize: 11 }} />
          <YAxis stroke="#9CA3AF" tick={{ fontSize: 11 }} unit={metric === 'win_rate' ? '%' : ''} />
          <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--surface-elevated))', border: '1px solid hsl(var(--border))', borderRadius: 8, color: '#fff' }} />
          <Legend itemSorter={(item) => ORDER.indexOf(item.dataKey)} />
          {names.map((n) => (
            <Line key={n || 'unset'} type="monotone" dataKey={n || UNSET_LABEL} stroke={PROVIDER_COLORS[n]} strokeWidth={2} dot={{ r: 3 }} connectNulls />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default function SignalProviderPerformance({ ibId, dateFrom, dateTo }) {
  const [channel, setChannel] = useState('all');
  const [granularity, setGranularity] = useState('day');
  const [metric, setMetric] = useState('net_pips');
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const start = dateFrom ? format(dateFrom, 'yyyy-MM-dd') : '';
  const end = dateTo ? format(dateTo, 'yyyy-MM-dd') : '';
  const load = useCallback(async () => {
    if (!ibId) return;
    setErr('');
    setLoading(true);
    try {
      const r = await axios.get(`${API}/signal-providers/${ibId}/performance`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        params: { start_date: start, end_date: end, channel, granularity },
      });
      setD(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Gagal load prestasi signal provider');
    } finally {
      setLoading(false);
    }
  }, [ibId, start, end, channel, granularity]);
  useEffect(() => { load(); }, [load]);
  const byName = useMemo(() => Object.fromEntries((d?.providers || []).map((r) => [r.provider, r])), [d]);
  const unset = byName[''];
  const names = useMemo(() => [...SIGNAL_PROVIDERS.filter((p) => byName[p]), ...(unset ? [''] : [])], [byName, unset]);

  return (
    <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden" data-testid="signal-provider-performance">
      <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/15 to-transparent flex flex-wrap items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40">
          <Radio size={20} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-white font-heading">Prestasi Signal Provider</h3>
          <p className="text-xs text-[#9CA3AF]">Siapa bagi signal & prestasi masing-masing · {start} → {end}</p>
        </div>
        <Toggle value={channel} onChange={setChannel} testid="provider-channel" options={[['all', 'Semua'], ['public', 'Public'], ['vip', 'VIP']]} />
      </div>
      <div className="p-4 sm:p-6 space-y-5">
        {err && (
          <div className="text-sm text-red-400 space-y-2" data-testid="provider-error">
            <p>{err}</p>
            <Button variant="outline" className="h-10" onClick={load} data-testid="provider-retry"><RefreshCw size={14} className="mr-1.5" /> Cuba lagi</Button>
          </div>
        )}
        {!err && !d && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="provider-loading">{SIGNAL_PROVIDERS.map((p) => <div key={p} className="h-48 rounded-xl bg-white/[0.04] animate-pulse" />)}</div>}
        {d && (
          <div className={`space-y-5 transition-opacity ${loading ? 'opacity-50' : ''}`} aria-busy={loading} data-testid="provider-body">
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#9CA3AF]" data-testid="provider-totals">
              <span>Jumlah signal: <b className="text-white">{d.totals.signals}</b></span>
              <span>Win rate channel: <b className="text-white">{fmt(d.totals.win_rate, '%')}</b></span>
              <span>Net pips channel: <b className={d.totals.net_pips >= 0 ? 'text-emerald-400' : 'text-red-400'}>{signed(d.totals.net_pips)}</b></span>
            </div>
            {unset && (
              <p className="text-sm text-amber-400 flex items-start gap-2" data-testid="provider-unset-warning">
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                {unset.signals} signal belum ditetapkan provider. Isi di Daily Data → Operation (Select Multiple Days → Set Provider) supaya laporan lengkap.
              </p>
            )}
            <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${unset ? 'xl:grid-cols-6' : 'xl:grid-cols-5'} gap-3`} data-testid="provider-cards">
              {SIGNAL_PROVIDERS.map((p) => <ProviderCard key={p} name={p} r={byName[p]} />)}
              {unset && <ProviderCard name="" r={unset} />}
            </div>
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-white">Trend setiap provider</p>
                <div className="flex flex-wrap gap-2">
                  <Toggle value={metric} onChange={setMetric} testid="provider-metric" options={[['net_pips', 'Net pips'], ['win_rate', 'Win rate %'], ['signals', 'Bil. signal']]} />
                  <Toggle value={granularity} onChange={setGranularity} testid="provider-granularity" options={[['day', 'Harian'], ['month', 'Bulanan']]} />
                </div>
              </div>
              <TrendChart trend={d.trend} metric={metric} names={names} />
            </div>
            <p className="text-xs text-[#6B7280]">{d.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}
